import { useEffect, useRef, useState } from 'react'
import { usePermissions } from '@/hooks/usePermissions'
import { PERMISSIONS } from '@/lib/permissions'
import type { SlotStartBody, UnavailabilityDto } from '../../../../lib/api/v2/planning'
import { cn } from '../../../../lib/utils'

interface AbsencePopoverProps {
  x: number
  y: number
  userId: string
  label: string
  /** Prefill for edit mode (the page deletes the old absence and recreates it on submit). */
  initial?: { reason: string; fromIso: string; toIso: string }
  onClose: () => void
  /** Create the absence: returns once the API call resolves (so the page can refresh + close). */
  onCreate: (body: { userId: string; label: string; durationUnit: 'Hours' | 'Days'; durationValue: number } & SlotStartBody) => Promise<void>
}

const todayIso = () => new Date().toISOString().slice(0, 10)

/** Common reasons offered as one-click presets — the field stays free text. */
const PRESETS = ['Congé', 'Maladie', 'Formation'] as const

/**
 * Add an employee absence (SOLY-PLAN-001 MS-04 / round 3). Anchored popover: a free-text reason
 * (with quick presets) + date range. Sent as a Days-unit unavailability (inclusive day count) from
 * its first day — a calendar date, no time. Esc / click-out closes.
 */
export function AbsencePopover({ x, y, userId, label, initial, onClose, onCreate }: AbsencePopoverProps) {
  const [reason, setReason] = useState(initial?.reason ?? '')
  const [from, setFrom] = useState(initial?.fromIso ?? todayIso())
  const [to, setTo] = useState(initial?.toIso ?? todayIso())
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    const onClick = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) onClose() }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onClick)
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onClick) }
  }, [onClose])

  const submit = async () => {
    if (submitting) return
    const trimmed = reason.trim()
    if (!trimmed) { setError('Indiquez un motif (ex : Congé, Maladie)'); return }
    if (to < from) { setError('La date de fin précède le début'); return }
    setSubmitting(true)
    setError(null)
    try {
      // Congé/maladie = whole days. Inclusive day count from the date range.
      const days = Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000) + 1
      await onCreate({
        userId,
        label: trimmed,
        durationUnit: 'Days',
        durationValue: days,
        startDate: from,
        startTime: null,
      })
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Échec de la création')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div
      ref={ref}
      data-testid="planning-absence-popover"
      className="fixed z-50 w-64 rounded-lg border bg-background p-3 shadow-lg"
      style={{ top: Math.min(y, window.innerHeight - 280), left: Math.min(x, window.innerWidth - 280) }}
      onKeyDown={(e) => { if (e.key === 'Enter') void submit() }}
    >
      <div className="mb-2 text-xs font-semibold text-muted-foreground">Absence · {label}</div>
      <div className="mb-2 flex flex-wrap gap-1">
        {PRESETS.map((p) => (
          <button key={p} type="button" data-testid={`planning-absence-preset-${p}`} onClick={() => setReason(p)}
            className={cn('rounded px-2 py-1 text-xs', reason === p ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground')}>
            {p}
          </button>
        ))}
      </div>
      <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Motif (ex : Congé, Maladie…)"
        data-testid="planning-absence-label" className="mb-2 w-full rounded border px-2 py-1 text-xs" />
      <div className="mb-2 flex items-center gap-2">
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="planning-absence-from" className="w-full rounded border px-2 py-1 text-xs" />
        <span className="text-xs text-muted-foreground">→</span>
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="planning-absence-to" className="w-full rounded border px-2 py-1 text-xs" />
      </div>
      {error && <p className="mb-2 text-[11px] text-red-600" data-testid="planning-absence-error">{error}</p>}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onClose} className="rounded px-2 py-1 text-xs text-muted-foreground">Annuler</button>
        <button type="button" onClick={() => void submit()} disabled={submitting} data-testid="planning-absence-create"
          className="rounded bg-primary px-3 py-1 text-xs text-primary-foreground disabled:opacity-50">Enregistrer</button>
      </div>
    </div>
  )
}

/** Inclusive yyyy-MM-dd range of an unavailability — its calendar days, as the DTO carries them. */
export function absenceDateRange(absence: UnavailabilityDto): { fromIso: string; toIso: string } {
  const fromIso = absence.startDate
  const toIso = absence.endDate
  return { fromIso, toIso: toIso < fromIso ? fromIso : toIso }
}

interface AbsenceDetailPopoverProps {
  x: number
  y: number
  absence: UnavailabilityDto
  /** Display name of the absent employee. */
  userLabel: string
  onClose: () => void
  /** Open the edit popover (AbsencePopover prefilled) — the page deletes + recreates on submit. */
  onEdit: (absence: UnavailabilityDto) => void
  onDelete: (absence: UnavailabilityDto) => Promise<void>
}

/**
 * Read-only card for an existing absence, opened by clicking its agenda cell: reason, date range,
 * and the Modifier / Supprimer actions that were missing (create-only before). Esc / click-out closes.
 */
export function AbsenceDetailPopover({ x, y, absence, userLabel, onClose, onEdit, onDelete }: AbsenceDetailPopoverProps) {
  const { can } = usePermissions()
  const canManage = can(PERMISSIONS.PLANNING_MANAGE)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    const onClick = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) onClose() }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onClick)
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onClick) }
  }, [onClose])

  const { fromIso, toIso } = absenceDateRange(absence)
  const fmt = (iso: string) => new Date(`${iso}T12:00:00Z`).toLocaleDateString('fr-BE', { day: '2-digit', month: 'short', timeZone: 'UTC' })

  const remove = async () => {
    if (submitting) return
    setSubmitting(true)
    setError(null)
    try {
      await onDelete(absence)
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Échec de la suppression')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div
      ref={ref}
      data-testid="planning-absence-detail"
      className="fixed z-50 w-64 rounded-lg border bg-background p-3 shadow-lg"
      style={{ top: Math.min(y, window.innerHeight - 200), left: Math.min(x, window.innerWidth - 280) }}
    >
      <div className="mb-1 text-xs font-semibold text-muted-foreground">Absence · {userLabel}</div>
      <div className="mb-1 text-sm font-medium">{absence.label}</div>
      <div className="mb-3 text-xs text-muted-foreground">
        {fromIso === toIso ? fmt(fromIso) : `${fmt(fromIso)} → ${fmt(toIso)}`}
      </div>
      {error && <p className="mb-2 text-[11px] text-red-600" data-testid="planning-absence-detail-error">{error}</p>}
      {/* SOLY-PERM-001 — the absence stays readable (« qui est absent quand » is the point of the
          agenda); deleting or moving someone's leave needs `planning:manage`. */}
      <div className="flex items-center justify-between">
        {canManage ? (
          <button type="button" onClick={() => void remove()} disabled={submitting} data-testid="planning-absence-delete"
            className="text-[11px] text-red-600 hover:underline disabled:opacity-50">Supprimer</button>
        ) : <span />}
        <div className="flex gap-2">
          <button type="button" onClick={onClose} className="rounded px-2 py-1 text-xs text-muted-foreground">Fermer</button>
          {canManage && (
            <button type="button" onClick={() => onEdit(absence)} data-testid="planning-absence-edit"
              className="rounded bg-primary px-3 py-1 text-xs text-primary-foreground">Modifier</button>
          )}
        </div>
      </div>
    </div>
  )
}
